import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import {
  Listing,
  ListingType,
  OWNER_OF_THE_BUSINESS,
  COMMERCIAL_PROPERTY,
  SALE_TYPE,
  TEASER_VERIFICATION,
  TEASER_TYPE,
  PAYMENT_OPTION,
  SUCCESSION,
  INCLUDE_VIDEO_IN_TEASER,
  RevenueModel,
} from '../../../../../models/Listing';
import utils, {
  getCheckedValuesInObject,
  getIsListingOwner,
  getListingTypeLabel,
  getSellingPortfolio,
} from 'utils';
import api from '../../../../../api';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import {
  MONEY_PROPERTIES,
  checkIfTeaserIsEnabled,
  extractRequiredVisibleInfoFields,
  getRevenueModel,
  mapDeadlineToFormValues,
} from '../../utils';
import { useTranslation } from 'react-i18next';
import ListingContext from './Listing.context';
import DropzoneRequestsProvider from 'providers/DropzoneRequests';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BUSINESS_ROUTE, LCW_ROUTE_PATHS } from 'data/global';
import PopNotificationsContext from '../../../../../providers/PopNotifications/PopNotifications.context';
import ResolveLoader from 'components/ResolveLoader';
import { YesNo } from 'models/Fields';
import { ASKING_PRICE_SELECT, STOCK_AT_VALUE } from 'models/DealRoom';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import { StripeError } from '@stripe/stripe-js';
import { GET_BUYERS_OPTIONS } from '../../components/GetBuyers/GetBuyers.component';
import mixpanelService from 'services/mixpanelService';
import { ListingCreationInitiatedEventProps } from 'models/MixpanelEvents';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import ListingFieldsRequiredVisibleInfoProvider from '../ListingFieldsRequiredVisibleInfo';

export type ListingProviderProps = {
  children?: React.ReactNode;
  listingType: ListingType;
};

// CREATE LISTING PROVIDER
const ListingProvider: React.FC<ListingProviderProps> = (props) => {
  const { children, listingType } = props;

  const [completedSteps, setCompletedSteps] = useState(0);
  const [payment, setPayment] = useState<() => Promise<StripeError>>();
  const [listing, setListing] = useState<Partial<Listing>>();
  const [listingIsFetching, setListingIsFetching] = useState(false);

  const [dealName, setDealName] = useState('');

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    isSuperAdmin,
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);

  const { dealRoomId } = useParams<{ dealRoomId: string }>();

  const { pathname } = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const getMixpanelProps = useGetMixpanelProps();

  const defaultDeadlineValue = useMemo(
    () => [{ timeline: t('seekingExpressionsOfInterestBy') }],
    [t],
  );

  const listingFormValues = useMemo(() => {
    if (!listing) return null;

    const {
      deadline,
      listingContact,
      askingPriceSelect,
      stockAtValue,
      highlights,
      adjustments,
      products,
      keyAssets,
      priceDescription,
      ownerOfTheBusiness,
      someoneToReplaceOwner,
      hasCommercialProperty,
      commercialProperty,
      saleType,
      contractedCustomers,
      revenueModel,
      enableTeaser,
      teaserVerification,
      teaserType,
      includeVideoInTeaser,
      listingPackage,
      succession,
      isEnterprise,
      ...restValues
    } = listing;

    const mappedValues = Object.entries(
      extractRequiredVisibleInfoFields(restValues),
    ).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [key]:
          MONEY_PROPERTIES.some((el) => key === el) && value
            ? utils.getDotedNumber(value as number | string)
            : value,
      };
    }, {});

    const newDeadline = deadline.length
      ? mapDeadlineToFormValues(deadline)
      : defaultDeadlineValue;

    const newAdjustments = adjustments?.length
      ? adjustments.map(({ netAmount, addBack, grossAmount, name }) => ({
          name,
          netAmount: utils.getDotedNumber(netAmount),
          addBack: utils.getDotedNumber(addBack),
          grossAmount: utils.getDotedNumber(grossAmount),
        }))
      : Array(1).fill(void 0);

    return {
      listingContact: listingContact,
      askingPriceSelect: askingPriceSelect || ASKING_PRICE_SELECT.FIXED_PRICE,
      stockAtValue: stockAtValue || STOCK_AT_VALUE.EXCLUDE_STOCK_AT_VALUE,
      highlights: highlights.length ? highlights : Array(3).fill(void 0),
      products: products.length ? products : Array(2).fill(void 0),
      keyAssets: keyAssets.length ? keyAssets : Array(2).fill(void 0),
      adjustments: newAdjustments,
      deadline: newDeadline,
      includeVideoInTeaser: includeVideoInTeaser || INCLUDE_VIDEO_IN_TEASER.NO,
      hasCommercialProperty: hasCommercialProperty || false,
      commercialProperty: commercialProperty || COMMERCIAL_PROPERTY.SELLING,
      revenueModel: getRevenueModel(revenueModel),
      ...(checkIfTeaserIsEnabled(listing)
        ? { enableTeaser }
        : { enableTeaser: true }),
      teaserVerification:
        teaserVerification || TEASER_VERIFICATION.WITHOUT_EMAIL_VERIFICATION,
      teaserType: teaserType || TEASER_TYPE.RESOLVE_DIGITAL_TEASER,
      priceDescription: priceDescription || 'actual',
      listingPackage,
      isEnterprise,
      getBuyers: listingPackage
        ? GET_BUYERS_OPTIONS.RESOLVE_BUYERS
        : GET_BUYERS_OPTIONS.PRIVATE,
      ...(!isEnterprise
        ? {
            contractedCustomers: contractedCustomers ? YesNo.Yes : YesNo.No,
            saleType: saleType || SALE_TYPE.SHARE,
            ownerOfTheBusiness:
              ownerOfTheBusiness || OWNER_OF_THE_BUSINESS.SILENT,
            someoneToReplaceOwner: someoneToReplaceOwner ? YesNo.Yes : YesNo.No,
          }
        : {
            saleType,
            contractedCustomers:
              typeof contractedCustomers === 'boolean'
                ? contractedCustomers
                  ? YesNo.Yes
                  : YesNo.No
                : null,
            succession: succession || SUCCESSION.GM_IN_PLACE,
          }),
      ...mappedValues,
    };
  }, [defaultDeadlineValue, listing]);

  useEffect(() => {
    setCompletedSteps(listing?.stepsCompleted || 0);
  }, [listing]);

  const isOnPreviewModeRoute = useMemo(
    () => pathname.includes('preview-mode'),
    [pathname],
  );

  useEffect(() => {
    (async () => {
      setListingIsFetching(true);

      try {
        const {
          data: { listing, name },
        } = await api.dealRoom.getRoom(dealRoomId, listingType);

        const canEditListing =
          isSuperAdmin ||
          getIsListingOwner(currentUserId, listing?.userId, listing?.isCoOwner);

        if (!canEditListing) navigate(BUSINESS_ROUTE);

        setDealName(name);
        setListing(listing);

        const { currentStep, stepsCompleted } = listing;
        const goToStep = currentStep ?? stepsCompleted;

        !isOnPreviewModeRoute &&
          navigate(`${LCW_ROUTE_PATHS[goToStep]}`, {
            replace: true,
          });
      } catch (e) {
        popServerError(e);
        navigate(BUSINESS_ROUTE, { replace: true });
      } finally {
        setListingIsFetching(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingType]);

  const patchListingStep = useCallback(
    async (
      currentStep: string,
      value: Partial<Listing> & { getBuyers?: string },
    ) => {
      if (!listing) return;

      const {
        someoneToReplaceOwner,
        hasCommercialProperty,
        commercialProperty,
        enableTeaser,
        teaserFile,
        embeddedTeaserVideoFile,
        EBITDAMultiple,
        EBITDA,
        accountingPeriod,
        getBuyers,
        contractedCustomers,
        saleType,
        isEnterprise,
        revenueModel,
        ...rest
      } = value;

      const newValues = {
        ...(hasCommercialProperty ? { commercialProperty } : {}),
        ...(someoneToReplaceOwner
          ? {
              someoneToReplaceOwner: someoneToReplaceOwner === YesNo.Yes,
            }
          : {}),
        ...(contractedCustomers
          ? {
              contractedCustomers: contractedCustomers === YesNo.Yes,
            }
          : {}),
        ...(saleType && {
          saleType,
        }),
        ...(checkIfTeaserIsEnabled(value) ? { enableTeaser } : {}),
        ...(accountingPeriod ? { EBITDAMultiple: EBITDAMultiple || null } : {}),
        ...(revenueModel
          ? {
              revenueModel: getCheckedValuesInObject(
                revenueModel,
              ) as RevenueModel[],
            }
          : {}),
        hasCommercialProperty,
        EBITDA,
        accountingPeriod,
        ...extractRequiredVisibleInfoFields(rest),
      };

      const {
        data: { listing: listingResponse },
      } = await api.listings.updateListingByStep(
        listing._id,
        currentStep,
        newValues,
      );

      setListing(listingResponse);
    },
    [listing],
  );

  const submitListing = useCallback(
    async (values) => {
      const { paymentOption, taxInvoice } = values;

      const isInvoice = paymentOption === PAYMENT_OPTION.INVOICE;

      if (!isInvoice) {
        const stripeError = await payment();

        if (stripeError) return;
      }

      try {
        mixpanelService.listingCreationInitiated(
          getMixpanelProps({
            dealId: dealRoomId,
            industry: listing.industry,
            isPaid: true,
            dealName,
            listingName: listing.listingTitle,
            listingPackage: listing.listingPackage,
            mediaPackage: listing.mediaPackage,
            outreach: listing.buyerOutreach,
            listingType: t(getListingTypeLabel(listingType)),
          }) as ListingCreationInitiatedEventProps,
        );

        await api.listings.publishListing(
          listing._id,
          isInvoice,
          !isInvoice ? taxInvoice : undefined,
        );
        navigate(getSellingPortfolio(dealRoomId, 'listing-information'));
        popSuccess(t(SUCCESS_MESSAGES.LISTING_PAY));
      } catch (e) {
        popServerError(e);
      }
    },
    [
      dealRoomId,
      listing,
      listingType,
      dealName,
      getMixpanelProps,
      navigate,
      payment,
      popServerError,
      popSuccess,
      t,
    ],
  );

  const uploadListingFile: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      if (!listing) return;

      const { uploadedFile } = await api.listings.uploadFile(
        file,
        fileName,
        listing._id,
        config,
      );
      return uploadedFile;
    },
    [listing],
  );

  const deleteListingFile: DropzoneDelete = useCallback(
    async (file, fileName) => {
      if (!listing) return;

      const {
        data: { fileId },
      } = await api.listings.deleteFile(listing._id, fileName, file.id);
      return fileId;
    },
    [listing],
  );

  return (
    <ListingContext.Provider
      value={{
        listing,
        dealName,
        listingType,
        completedSteps,
        listingFormValues,
        patchListingStep,
        setPayment,
        setListing,
        submitListing,
      }}
    >
      {listingIsFetching || !listing ? (
        <ResolveLoader overlayColor="transparent" />
      ) : (
        <ListingFieldsRequiredVisibleInfoProvider
          listing={listing}
          canChangeRequired
        >
          <DropzoneRequestsProvider
            uploadRequest={uploadListingFile}
            deleteRequest={deleteListingFile}
          >
            {children}
          </DropzoneRequestsProvider>
        </ListingFieldsRequiredVisibleInfoProvider>
      )}
    </ListingContext.Provider>
  );
};

export default ListingProvider;
